<template>
  <BCard v-if="event" class="event-card">
    <NuxtLink :to="eventLink">
      <BCardImg
        v-if="event.image_small"
        :src="String(event.image_small)"
        class="rounded mb-2"
        placement="top"
        fluid
        lazy
        :alt="event.title ?? ''"
      />
    </NuxtLink>
    <EventActions @reg_action="$emit('reg_action', $event)" :event="event" />
    <NuxtLink :to="eventLink" class="title">{{ event.title }}</NuxtLink>

    <BCardText tag="div">
      <div>
        <span v-if="event.human_date" class="me-2 d-inline-block">
          <Icon name="fa6-regular:calendar" class="icon-inline" />
          {{ event.human_date }}
        </span>
        <span v-if="event.human_time" class="d-inline-block">
          <Icon name="fa6-regular:clock" class="me-1 icon-inline" />
          {{ event.human_time }}
        </span>
      </div>

      <span v-if="event.place"><b>{{ t('Where') }}:</b> {{ event.place }} <br /></span>
      <span v-if="event.meeting_place"><b>{{ t('Meeting place') }}:</b> {{ event.meeting_place }} <br /></span>
      <span v-if="event.when"><b>{{ t('When') }}:</b> {{ event.when }} <br /></span>
      <span v-if="event.working_time"><b>{{ t('Working time') }}:</b> {{ event.working_time }} <br /></span>
    </BCardText>
  </BCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import EventActions, { type EventReg } from './EventActions.vue'

interface EventCardEvent extends EventReg {
  image_small?: string
  human_date?: string
  human_time?: string
  place?: string
  meeting_place?: string
  when?: string
  working_time?: string
}

const props = withDefaults(
  defineProps<{
    event: EventCardEvent | null
    /** Localized list page path from parent, e.g. `localePath('/vip/program/')` — links use `?event=id` on this path */
    eventListPath: string
  }>(),
  {
    event: null,
  },
)

defineEmits<{
  reg_action: [payload: unknown]
}>()

const { t } = useI18n({ useScope: 'local' })

const eventLink = computed((): RouteLocationRaw | string => {
  if (!props.event?.id || !props.eventListPath) return ''
  return {
    path: props.eventListPath,
    query: { event: String(props.event.id) },
  }
})
</script>

<i18n lang="json">
{
  "en": {
    "Where": "Where",
    "Meeting place": "Meeting place",
    "When": "When",
    "Working time": "Working time"
  },
  "ru": {
    "Where": "Адрес",
    "Meeting place": "Место встречи",
    "When": "Когда",
    "Working time": "Время работы"
  }
}
</i18n>

<style lang="scss" scoped>
.icon-inline {
  vertical-align: -0.125em;
}

.event-card {
  margin-bottom: 2rem;
  border: none;
  display: inline-block;
  :deep(.card-body) {
    padding: 0 0 1rem 0;
  }
  .title {
    color: black;
    margin: 1rem 0;
    display: inline-block;
    font-weight: bold;
  }
}
</style>
